// src/features/services/media-v2/components/YouTubeIngestPanel.tsx
// ─────────────────────────────────────────────────────────────────────────────
// YouTube URL ingest — submit a URL, watch real-time SSE progress.
// PAC Glass design system.
// ─────────────────────────────────────────────────────────────────────────────
import React, { useState, useCallback } from 'react';
import { useYoutubeIngest } from '../hooks/useMediaV2';

const STAGE_LABELS: Record<string, string> = {
  triggering:  'Triggering download…',
  dispatched:  'Queued for processing',
  'step:validate': 'Validating URL',
  'step:download': 'Downloading audio…',
  'step:register': 'Registering entity',
  complete:    'Download complete',
  failed:      'Download failed',
};

export default function YouTubeIngestPanel() {
  const [url, setUrl] = useState('');
  const { ingest, streaming, clearStream } = useYoutubeIngest();

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      const trimmed = url.trim();
      if (!trimmed) return;
      ingest.mutate({ url: trimmed });
    },
    [url, ingest],
  );

  return (
    <div className="pac-glass rounded-lg p-4 space-y-4">
      <h2 className="text-sm font-medium text-neutral-300">YouTube → Audio</h2>

      <form onSubmit={handleSubmit} className="flex gap-2">
        <input
          type="text"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="https://youtube.com/watch?v=..."
          className="flex-1 bg-neutral-900 border border-neutral-700 text-neutral-200
                     rounded px-3 py-1.5 text-sm placeholder:text-neutral-600
                     focus:outline-none focus:border-cyan-600"
          disabled={ingest.isPending || streaming.active}
        />
        <button
          type="submit"
          disabled={ingest.isPending || streaming.active || !url.trim()}
          className="px-4 py-1.5 text-sm font-medium rounded bg-cyan-700 text-neutral-100
                     hover:bg-cyan-600 disabled:opacity-40 disabled:cursor-not-allowed
                     transition-colors"
        >
          {ingest.isPending ? 'Sending…' : 'Download'}
        </button>
      </form>

      {ingest.isError && (
        <p className="text-xs text-red-400">{ingest.error.message}</p>
      )}

      {/* SSE pipeline progress */}
      {streaming.active && streaming.scopeKey && (
        <div className="pac-glass-raised rounded p-3 space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-xs text-neutral-400">
              Pipeline: <span className="text-cyan-400">{streaming.scopeKey}</span>
            </span>
            <button
              onClick={clearStream}
              className="text-[10px] text-neutral-600 hover:text-neutral-400"
            >
              dismiss
            </button>
          </div>

          <div className="space-y-1">
            {streaming.events.map((ev, i) => (
              <div key={i} className="flex items-center gap-2 text-xs">
                <span className={`w-1.5 h-1.5 rounded-full ${
                  ev.stage === 'failed' ? 'bg-red-500' :
                  ev.stage === 'complete' ? 'bg-green-500' :
                  'bg-cyan-500 animate-pulse'
                }`} />
                <span className="text-neutral-400">
                  {STAGE_LABELS[ev.stage] ?? ev.stage}
                </span>
              </div>
            ))}
          </div>

          {streaming.error && (
            <p className="text-xs text-red-400 mt-1">{streaming.error}</p>
          )}
        </div>
      )}
    </div>
  );
}
